import React from 'react';
import clsx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { useLocation, useNavigate, useOutlet, useOutletContext, useParams } from 'react-router-dom';
import * as Headless from '@headlessui/react';

import { PencilSquareIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';

import {
  CaraWorkspaceUseCase,
  ClientField,
  type ClientFieldsFragment,
  useGetClientOverviewQuery,
  useGetClientQuery
} from '~/api/graphql';
import { ClientFormDialog } from '~/components/client-form';
import { ClientActivity } from '~/components/clients/client-activity';
import { ClientAmsAccountField } from '~/components/clients/client-ams-account';
import { ClientCard, ClientTabRoute } from '~/components/clients/client-panels';
import { Dash, formatAddress } from '~/components/clients/clients-columns';
import { FieldSourceTag, provenanceByField } from '~/components/clients/client-field-provenance';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { dateFormatter } from '~/components/table-formatters';
import { useDefaultVisibilityValue } from '~/components/visibility/default-visibility';
import { PresetRunPage } from '~/pages/presets/presets';
import { useCreateCaraWorkspace } from '~/components/workspaces/use-create-cara-workspace';
import { UseCaseRunCard } from '~/components/home/run-card-dialog';
import { clientSummary } from '~/components/home/run-card-footer';
import { UseCaseBrowser } from '~/components/home/use-case-browser';
import {
  AVAILABLE_USE_CASES,
  hasRequiredLibrary,
  type LineOfBusiness,
  type UseCaseSpec
} from '~/components/home/use-case-catalog';
import {
  useHomeLauncher,
  type HomeLauncherSources,
  type HomeTile
} from '~/components/home/use-home-launcher';
import { CustomPresetChip, UseCaseChip } from '~/components/home/use-case-tile';
import { pinKey } from '~/api/data/home/use-pinned-home-items';

/**
 * Raises a possible duplicate where somebody will meet it. Names the other
 * record rather than saying a duplicate exists, because the name is what decides
 * whether it is worth opening the queue at all.
 */
export const DuplicatePrompt = (props: {
  clientId: string;
  candidates: {
    id: string;
    clientA: { id: string; name: string };
    clientB: { id: string; name: string };
  }[];
}) => {
  if (props.candidates.length === 0) {
    return null;
  }

  const others = props.candidates.map((candidate) =>
    candidate.clientA.id === props.clientId ? candidate.clientB : candidate.clientA
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-500/40 dark:bg-amber-500/10">
      <p className="text-sm text-amber-900 dark:text-amber-200">
        {others.length === 1 ? (
          <>
            This client may be the same as <strong>{others[0].name}</strong>.
          </>
        ) : (
          <>
            This client may be the same as <strong>{others[0].name}</strong> and {others.length - 1}{' '}
            other{others.length - 1 === 1 ? '' : 's'}.
          </>
        )}
      </p>
      <Button outline href="/app/clients/duplicates">
        Review
      </Button>
    </div>
  );
};

/**
 * One labelled field in the profile block. Renders a dash for anything missing,
 * because a client whose agency has no synced AMS data legitimately has a name
 * and nothing else.
 *
 * The source tag sits on the label, where a long address cannot push it off
 * the row.
 */
const ProfileField = (props: {
  label: string;
  source?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <div>
    <dt className="flex items-center gap-x-2 text-xs font-medium text-neutral-500 dark:text-neutral-400">
      {props.label}
      {props.source}
    </dt>
    <dd className="mt-1 text-sm text-neutral-900 dark:text-neutral-100">
      {props.children ?? <Dash />}
    </dd>
  </div>
);

/** How many presets the Overview offers before the browser takes over. */
const LAUNCHER_SIZE = 6;

const clientLines = (client: ClientFieldsFragment): LineOfBusiness[] => [
  ...(client.isCommercial ? (['commercial'] as const) : []),
  ...(client.isPersonal ? (['personal'] as const) : [])
];

/**
 * The user's own pins first, since those are what they run, topped up from the
 * catalog with what fits this client's lines of business. A client with no
 * recorded line takes the catalog in order.
 */
export const launcherTiles = (
  pinned: HomeTile[],
  lines: LineOfBusiness[],
  isRunnable: (useCase: UseCaseSpec) => boolean
): HomeTile[] => {
  const taken = new Set(pinned.map((tile) => pinKey(tile.pin)));
  const suggestions = AVAILABLE_USE_CASES.filter(
    (useCase) =>
      !taken.has(pinKey({ kind: 'useCase', id: useCase.id })) &&
      isRunnable(useCase) &&
      (lines.length === 0 || useCase.lines.some((line) => lines.includes(line)))
  ).map<HomeTile>((useCase) => ({ pin: { kind: 'useCase', id: useCase.id }, useCase }));
  return [...pinned, ...suggestions].slice(0, LAUNCHER_SIZE);
};

const LauncherRow = (props: {
  chip: React.ReactNode;
  title: string;
  description: string;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
}) => (
  <li>
    <Headless.Button
      type="button"
      disabled={props.disabled}
      aria-label={props.disabled ? `${props.title} (${props.disabledReason})` : props.title}
      onClick={props.onClick}
      className={clsx(
        'flex w-full items-start gap-3 rounded-lg px-2 py-2 text-left',
        'hover:bg-neutral-50 dark:hover:bg-white/5',
        'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
      )}
    >
      {props.chip}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-neutral-900 dark:text-white">
          {props.title}
        </span>
        <span className="line-clamp-1 text-xs text-neutral-500 dark:text-neutral-400">
          {props.description}
        </span>
      </span>
    </Headless.Button>
  </li>
);

/**
 * Starts a preset for this client: the same run card and browser as the home
 * page, with the client already linked.
 */
const ClientPresetLauncher = ({
  client,
  sources
}: {
  client: ClientFieldsFragment;
  sources: HomeLauncherSources;
}) => {
  const navigate = useNavigate();
  const launcher = useHomeLauncher(sources);
  const defaultVisibility = useDefaultVisibilityValue();
  const { createWorkspace, error, clearError } = useCreateCaraWorkspace(defaultVisibility);

  const [browserOpen, setBrowserOpen] = React.useState(false);
  // Kept after close so the dialog still has content while it animates out.
  const [runCard, setRunCard] = React.useState<CaraWorkspaceUseCase | null>(null);
  const [runCardShowing, setRunCardShowing] = React.useState(false);

  const tiles = React.useMemo(
    () =>
      launcherTiles(launcher.tiles, clientLines(client), (useCase) =>
        hasRequiredLibrary(useCase, launcher.libraries)
      ),
    [client, launcher.libraries, launcher.tiles]
  );

  const account = React.useMemo(
    () => ({ id: client.id, name: client.name, summary: clientSummary(client) }),
    [client]
  );

  const openUseCase = (id: CaraWorkspaceUseCase) => {
    clearError();
    setBrowserOpen(false);
    setRunCard(id);
    setRunCardShowing(true);
  };

  const openCustomPreset = (id: string) => {
    setBrowserOpen(false);
    navigate(`presets/${id}`);
  };

  return (
    <ClientCard title="Start work" subtitle="Run a preset with this client already linked">
      <ul className="-mx-2 m-0 list-none space-y-0.5 p-0">
        {tiles.map((tile) =>
          'preset' in tile ? (
            <LauncherRow
              key={pinKey(tile.pin)}
              chip={<CustomPresetChip />}
              title={tile.preset.title}
              description={tile.preset.description}
              disabled={!launcher.canCreate}
              disabledReason="you cannot start workspaces"
              onClick={() => openCustomPreset(tile.preset.id)}
            />
          ) : (
            <LauncherRow
              key={pinKey(tile.pin)}
              chip={<UseCaseChip useCase={tile.useCase} />}
              title={tile.useCase.title}
              description={tile.useCase.description}
              disabled={!launcher.canCreate || (tile.useCase.pro && !launcher.hasProAccess)}
              disabledReason={
                launcher.canCreate ? 'requires the Pro plan' : 'you cannot start workspaces'
              }
              onClick={() => openUseCase(tile.useCase.id)}
            />
          )
        )}
      </ul>

      {launcher.canCreate && (
        <Button outline className="mt-4 w-full" onClick={() => setBrowserOpen(true)}>
          <Squares2X2Icon data-slot="icon" />
          Browse presets
        </Button>
      )}

      <AnimatePresence mode="wait" initial>
        {browserOpen && (
          <UseCaseBrowser
            key="use-case-browser"
            onClose={() => setBrowserOpen(false)}
            pinned={launcher.pins.pinned}
            isPinned={launcher.pins.isPinned}
            onTogglePin={launcher.pins.toggle}
            onRemovePin={launcher.pins.remove}
            onReorder={launcher.pins.reorder}
            customPresets={launcher.runnablePresets}
            libraries={launcher.libraries}
            onRun={openUseCase}
            onRunCustomPreset={openCustomPreset}
            hasProAccess={launcher.hasProAccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait" initial>
        {runCardShowing && runCard && (
          <UseCaseRunCard
            key="use-case-run-card"
            useCaseId={runCard}
            initialAccount={account}
            onClose={() => setRunCardShowing(false)}
            onChangeUseCase={setRunCard}
            createWorkspace={createWorkspace}
            error={error}
            hasProAccess={launcher.hasProAccess}
            libraries={launcher.libraries}
          />
        )}
      </AnimatePresence>
    </ClientCard>
  );
};

const ClientOverview = ({
  clientId,
  sources
}: {
  clientId: string;
  sources: HomeLauncherSources;
}) => {
  const outlet = useOutlet(sources);
  const location = useLocation();
  const [editing, setEditing] = React.useState(false);
  // Answered from the cache the route loader just filled.
  const showProvenance = useFlag(FEATURE_FLAGS.CLIENT_FIELD_PROVENANCE);
  const { data } = useGetClientOverviewQuery({
    variables: { id: clientId },
    fetchPolicy: 'cache-first'
  });

  const client = data?.client;
  if (!client) {
    return null;
  }

  const address = client.address ? formatAddress(client.address) : null;
  // The flag again, though the server already answers null when it is off: a
  // cache warmed before an agency was turned on would otherwise show chips the
  // rest of the session.
  const provenance = showProvenance ? provenanceByField(client.fieldProvenance ?? []) : {};
  const sourceTag = (field: ClientField) => {
    const tag = provenance[field];
    return tag ? <FieldSourceTag clientId={client.id} tag={tag} /> : null;
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="min-w-0 space-y-6 lg:col-span-2">
        <DuplicatePrompt clientId={client.id} candidates={client.pendingMergeCandidates} />

        <ClientCard
          title="Client profile"
          subtitle="Contact details and ownership"
          action={
            <Button plain className="-my-1.5" onClick={() => setEditing(true)}>
              <PencilSquareIcon data-slot="icon" />
              Edit
            </Button>
          }
        >
          <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            {/* The header shows the name too, but a tag belongs beside the
                fields the Edit dialog writes, and the header spans every tab. */}
            <div className="sm:col-span-2">
              <ProfileField label="Name" source={sourceTag(ClientField.Name)}>
                {client.name}
              </ProfileField>
            </div>
            <ProfileField label="Email" source={sourceTag(ClientField.Email)}>
              {client.email}
            </ProfileField>
            <ProfileField label="Phone" source={sourceTag(ClientField.Phone)}>
              {client.phone}
            </ProfileField>
            <ProfileField label="Owner">{client.agencyUserOwner?.name}</ProfileField>
            <ProfileField label="AMS account">
              <ClientAmsAccountField clientId={client.id} links={client.amsAccounts} />
            </ProfileField>
            <ProfileField label="Added">
              {dateFormatter.format(new Date(client.createdAt))}
            </ProfileField>
            <div className="sm:col-span-2">
              <ProfileField label="Address" source={sourceTag(ClientField.Address)}>
                {address}
              </ProfileField>
            </div>
          </dl>
        </ClientCard>

        <ClientCard title="Team" subtitle="Others at your agency who work this client">
          {client.agencyUsers.length === 0 ? (
            <p className="m-0 text-sm text-neutral-500 dark:text-neutral-400">
              No additional users are associated with this client.
            </p>
          ) : (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {client.agencyUsers.map((user) => (
                <li key={user.id}>
                  <Badge color="zinc">{user.name || user.email}</Badge>
                </li>
              ))}
            </ul>
          )}
        </ClientCard>

        <ClientActivity clientId={client.id} />
      </div>

      <div className="min-w-0">
        <ClientPresetLauncher client={client} sources={sources} />
      </div>

      {/* The mutation answers with the same fields the page reads, so the edit
          lands here, in the header and in the list without a refetch. */}
      <ClientFormDialog open={editing} client={client} onClose={() => setEditing(false)} />

      <AnimatePresence mode="wait" initial>
        {outlet && React.cloneElement(outlet, { key: location.pathname })}
      </AnimatePresence>
    </div>
  );
};

/** A custom preset run from the Overview, filed under this client. */
export const ClientPresetRunPage = () => {
  const { id = '' } = useParams();
  const { data } = useGetClientQuery({ variables: { id }, fetchPolicy: 'cache-first' });
  const sources = useOutletContext<HomeLauncherSources>();
  const client = data?.client;
  return (
    <PresetRunPage
      client={client ? { id: client.id, name: client.name } : undefined}
      library={sources}
    />
  );
};

type OverviewLoaderData = HomeLauncherSources & Record<string, unknown>;

/** The Overview tab: who the client is, what has happened for them, and what to start next. */
export const ClientOverviewTab = () => {
  const { id = '' } = useParams();
  return (
    <ClientTabRoute<OverviewLoaderData> noun="client">
      {(data) => (
        <ClientOverview
          clientId={id}
          sources={{
            agencyUser: data.agencyUser ?? null,
            customPresets: data.customPresets ?? [],
            customForms: data.customForms ?? [],
            htmlDocumentTemplates: data.htmlDocumentTemplates ?? [],
            wordDocumentTemplates: data.wordDocumentTemplates ?? [],
            excelSpreadsheetTemplates: data.excelSpreadsheetTemplates ?? []
          }}
        />
      )}
    </ClientTabRoute>
  );
};
